import { getAxiosInstance } from '../../react/utils/networkUtil';
import { Syncable } from '../models/Syncable';
import { PaginationQuery, PaginationResult } from '../models/AppPagination';
import { CollectItem_Base } from '../models/CollectItem';
import { User_Base } from '../models/User';

export const getCollectItemByCollectFormId = async (
  collectFormId: number,
  page: number,
  pageSize: number,
  keyword: string,
): Promise<any> => {
  const response = await getAxiosInstance().get(
    `/api/collect-items-ext?collectFormId=${collectFormId}&page=${page}&pageSize=${pageSize}${
      keyword !== '' ? `&keyword=${keyword}` : ''
    }`,
  );
  return response.data;
};

export const deleteCollectItem = async (id: number): Promise<any> => {
  const response = await getAxiosInstance().delete(`/api/collect-items-ext/${id}`);
  return response.data;
};

// get syncable page
export type CollectItem_GetSyncablePage_RequestQuery = PaginationQuery & {
  collect_form_uuid: string;
  no_deleted?: boolean;
  filters?: any; // Strapi filters object
  user_id?: number;
};
export type CollectItem_GetSyncablePage_Response = PaginationResult<Syncable>;
export const remote_getPageSyncable = async (
  request: CollectItem_GetSyncablePage_RequestQuery,
): Promise<CollectItem_GetSyncablePage_Response> => {
  const response = await getAxiosInstance().get(`/api/collect-items-ext/get-page-syncable`, {
    params: request,
  });
  return response.data;
};

// get list
export type CollectItem_GetListWithUUIDs_RequestBody = {
  collect_form_uuid: string;
  uuids: string[];
}
export type CollectItem_GetListWithUUIDs_ResponseItem = CollectItem_Base & {
  user: User_Base;
};
export type CollectItem_GetListWithUUIDs_Response = Array<CollectItem_GetListWithUUIDs_ResponseItem>;
// End get list
export const remote_getCollectItemsWithUUIDs = async (
  request: CollectItem_GetListWithUUIDs_RequestBody,
): Promise<CollectItem_GetListWithUUIDs_Response> => {
  const response = await getAxiosInstance().post(`/api/collect-items-ext/get-list-with-uuids`, request);
  return response.data;
};

// Export functionality
export type CollectItem_Export_RequestQuery = {
  form_uuid: string;
  format: 'ESRI Shapefile' | 'MapInfo File' | 'LIBKML' | 'XLSX' | 'GeoJSON';
  client_created_at_start?: string; // ISO date string
  client_created_at_end?: string;   // ISO date string
  uuids?: string[]; // Optional: export specific items by UUIDs
};

export type CollectItem_Export_Response = {
  status: 200;
  message: string;
  i18n: {
    vi: string;
    en: string;
  };
  data: {
    file_path: string;
    file_size: number;
    format: string;
    download_url: string;
  };
};

export const remote_exportCollectItems = async (
  request: CollectItem_Export_RequestQuery,
): Promise<CollectItem_Export_Response> => {
  const response = await getAxiosInstance().get(`/api/collect-items-ext/export`, {
    params: request,
  });
  return response.data;
};

// Get one collect item by UUID
export type CollectItem_GetOne_Response = CollectItem_Base & {
  user?: User_Base;
};

export const remote_getOne = async (
  collectItemUuid: string,
): Promise<CollectItem_GetOne_Response> => {
  const response = await getAxiosInstance().get(`/api/collect-items-ext/${collectItemUuid}`);
  return response.data;
};

// Update collect item
export type CollectItem_Update_RequestBody = {
  properties: Record<string, any>;
};

export type CollectItem_Update_Response = {
  status: 200;
  message: string;
  i18n: {
    vi: string;
    en: string;
  };
  data: CollectItem_GetOne_Response;
};

export const remote_update = async (
  collectItemUuid: string,
  requestBody: CollectItem_Update_RequestBody,
): Promise<CollectItem_Update_Response> => {
  const response = await getAxiosInstance().put(`/api/collect-items-ext/${collectItemUuid}`, requestBody);
  return response.data;
};

// Delete collect item (soft delete)
export type CollectItem_Delete_Response = {
  status: 200;
  message: string;
  i18n: {
    vi: string;
    en: string;
  };
  data: {
    uuid: string;
  };
};

export const remote_delete = async (
  collectItemUuid: string,
): Promise<CollectItem_Delete_Response> => {
  const response = await getAxiosInstance().delete(`/api/collect-items-ext/${collectItemUuid}`);
  return response.data;
};

export type CollectItem_BulkDelete_RequestBody = {
  uuids: string[];
};

export type CollectItem_BulkDelete_Response = {
  status: 200;
  message: string;
  i18n: {
    vi: string;
    en: string;
  };
  data: {
    deleted_count: number;
    uuids: string[];
  };
};

export const remote_bulkDelete = async (
  request: CollectItem_BulkDelete_RequestBody,
): Promise<CollectItem_BulkDelete_Response> => {
  const response = await getAxiosInstance().post(`/api/collect-items-ext/bulk-delete`, request);
  return response.data;
};

export const CollectItemAPIs = {
  getCollectItemByCollectFormId,
  deleteCollectItem,
  remote_getCollectItemsWithUUIDs,
  remote_getPageSyncable,
  remote_exportCollectItems,
  remote_getOne,
  remote_update,
  remote_delete,
  remote_bulkDelete,
}